import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getEnabledElement, Enums, eventTarget } from '@cornerstonejs/core';
import { classes } from '@ohif/core';

/**
 * ViewerSettings
 * --------------
 * A small, growable home for per-viewer behaviours that are NOT image filters.
 * Today it covers:
 *
 *   1. Orientation auto-correction — rotate/flip the viewport so a weld shot is
 *      shown the right way up, driven by the DICOM `PatientOrientation` tag.
 *   2. Window/Level persistence — saves the user's brightness/contrast for a
 *      shot, and restores it per shot on load/navigation.
 *
 * Add future lightweight viewer behaviours here rather than scattering them.
 */

// Pick the Django API origin automatically: local Django when the viewer runs
// on localhost, the production tenant host otherwise.
function getWindowingUrl(client) {
  const host = window.location.hostname;
  const isLocal =
    host === 'localhost' ||
    host.endsWith('.localhost') ||
    /^\d{1,3}(\.\d{1,3}){3}$/.test(host);
  const base = isLocal
    ? `http://${client}.localhost:8000`
    : `https://${client}.orbixnde.com`;
  return `${base}/api/windowing`;
}

// The viewer is launched with a short-lived RS256 JWT in the URL (?token=).
// Mode.tsx scrubs it from the URL shortly after load and stashes a copy in
// sessionStorage, so read it from either place and forward it as a Bearer token.
function getAuthHeaders() {
  let token = new URLSearchParams(window.location.search).get('token');
  if (!token) {
    try {
      token = sessionStorage.getItem('ohif_viewer_token');
    } catch (e) {
      token = null;
    }
  }
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Read saved window/level for a study (no width/level in the body -> restore).
async function fetchWindowingData(sopInstanceUID, studyInstanceUID) {
  const client = new URLSearchParams(window.location.search).get('client');
  const response = await fetch(getWindowingUrl(client), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ sopInstanceUID, studyInstanceUID }),
  });
  return response.json();
}

async function updateWindowingData(studyInstanceUID, sopInstanceUID, windowWidth, windowLevel) {
  const client = new URLSearchParams(window.location.search).get('client');
  const response = await fetch(getWindowingUrl(client), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ studyInstanceUID, sopInstanceUID, windowWidth, windowLevel }),
  });
  if (!response.ok) {
    throw new Error(`Windowing save failed with status ${response.status}`);
  }
  return response.json();
}

/**
 * Per-shot only: a shot is windowed *exclusively* with its own saved values and
 * never inherits another shot's window. Shots without their own saved values
 * keep the image's default display.
 */
function resolveWindowing(verificationResults, sopInstanceUID) {
  const windowWidthResult = verificationResults.find(
    r => r.sop_instance_uid === sopInstanceUID && r.window_width
  );
  const windowLevelResult = verificationResults.find(
    r => r.sop_instance_uid === sopInstanceUID && r.window_level
  );
  return { windowWidthResult, windowLevelResult };
}

// Keep re-applying the saved window/level for a short grace window after the
// first apply. Cornerstone applies the image's own default VOI when its pixel
// data loads (which races with, and overwrites, our setVOI on reload);
// re-asserting on IMAGE_RENDERED until the values match makes the saved
// windowing stick. Bounded in time + skipped once correct so it never fights
// the user's manual windowing afterwards.
function reassertVOI(viewport, lower, upper) {
  const el = viewport?.element;
  if (!el) {
    return;
  }
  const deadline = Date.now() + 3000;
  const onRendered = () => {
    if (Date.now() > deadline) {
      el.removeEventListener(Enums.Events.IMAGE_RENDERED, onRendered);
      return;
    }
    const cur = viewport.voiRange;
    if (!cur || Math.abs(cur.lower - lower) > 1 || Math.abs(cur.upper - upper) > 1) {
      viewport.setVOI({ lower, upper });
      viewport.render();
    }
  };
  el.addEventListener(Enums.Events.IMAGE_RENDERED, onRendered);
  setTimeout(() => el.removeEventListener(Enums.Events.IMAGE_RENDERED, onRendered), 3200);
}

function getCurrentImageId(viewport) {
  if (typeof viewport.getCurrentImageId === 'function') {
    const current = viewport.getCurrentImageId();
    if (current) {
      return current;
    }
  }
  const imageIds = viewport.imageIds;
  return imageIds && imageIds.length > 0 ? imageIds[0] : null;
}

// Fetch + apply the saved window/level for one shot. Returns:
//  'applied' (values set), 'none' (study has no saved values) or 'pending'
//  (couldn't resolve this shot's values — caller may retry).
async function restoreWindowingToViewport(viewport, sopInstanceUID, studyInstanceUID) {
  const windowingData = await fetchWindowingData(sopInstanceUID, studyInstanceUID);
  const verificationResults = windowingData.verification_results;

  if (!verificationResults || verificationResults.length === 0) {
    return 'none';
  }

  const { windowWidthResult, windowLevelResult } = resolveWindowing(
    verificationResults,
    sopInstanceUID
  );
  if (!windowWidthResult || !windowLevelResult) {
    return 'none';
  }

  const windowWidth = Number(windowWidthResult.window_width);
  const windowLevel = Number(windowLevelResult.window_level);
  const lower = windowLevel - windowWidth / 2;
  const upper = windowLevel + windowWidth / 2;

  viewport.setVOI({ lower, upper });
  viewport.render();
  reassertVOI(viewport, lower, upper);
  return 'applied';
}

// Map the DICOM PatientOrientation (row\column direction cosines, abbreviated)
// to the registered viewport command(s) that bring the image upright. 'H\\F' is
// a 180° turn, achieved by rotating 90° clockwise twice (there is no dedicated
// 180° command registered).
function adjustOrientation(dicomMetaData, commandsManager) {
  if (!commandsManager || !dicomMetaData?.PatientOrientation) {
    return;
  }

  const orientation = dicomMetaData.PatientOrientation.join('\\');
  const rotationMap = {
    'L\\F': ['rotateViewportCCW'],
    'R\\F': ['rotateViewportCW'],
    'H\\F': ['rotateViewportCW', 'rotateViewportCW'],
    'F\\F': ['flipViewportHorizontal'],
  };

  const commands = rotationMap[orientation];
  if (!commands) {
    return;
  }

  try {
    commands.forEach(commandName => commandsManager.runCommand(commandName));
  } catch (error) {
    console.warn('ViewerSettings: failed to run orientation command:', error);
  }
}

// Orientation commands are cumulative (each rotate adds to the current angle and
// each flip toggles), so we must apply orientation exactly ONCE per shot. Track
// the SOPs we've already corrected for the lifetime of the page.
const orientationApplied = new Set();

/**
 * Read-only restore of the saved window/level to the currently active viewport.
 * Exposed so navigation (thumbnail double-click) can re-apply after the new
 * display set has loaded.
 */
export async function applyWindowing() {
  const studyInstanceUID = new URLSearchParams(window.location.search).get('StudyInstanceUIDs');
  if (!studyInstanceUID) {
    return;
  }
  const viewportElement = document.querySelector('.cornerstone-viewport-element');
  if (!viewportElement) {
    return;
  }
  const enabledElement = getEnabledElement(viewportElement);
  if (!enabledElement || !enabledElement.viewport) {
    return;
  }
  const imageId = getCurrentImageId(enabledElement.viewport);
  if (!imageId) {
    return;
  }
  const sopInstanceUID = classes.MetadataProvider.get('instance', imageId)?.SOPInstanceUID;
  if (!sopInstanceUID) {
    return;
  }
  await restoreWindowingToViewport(enabledElement.viewport, sopInstanceUID, studyInstanceUID);
}

/**
 * Apply the saved windowing once the active viewport finishes loading a
 * newly-selected display set. Used by thumbnail double-click navigation:
 * setDisplaySetsForViewports loads the new image asynchronously, so we wait for
 * STACK_NEW_IMAGE before reading its SOP and applying. A timeout safety-net
 * covers the case where the same display set is reselected (no STACK_NEW_IMAGE).
 */
export function applyWindowingAfterLoad() {
  let handled = false;
  const cleanups = [];
  const runOnce = () => {
    if (handled) {
      return;
    }
    handled = true;
    cleanups.forEach(fn => fn());
    setTimeout(() => {
      applyWindowing();
    }, 50);
  };

  const viewportElement = document.querySelector('.cornerstone-viewport-element');
  if (viewportElement) {
    viewportElement.addEventListener(Enums.Events.STACK_NEW_IMAGE, runOnce);
    cleanups.push(() =>
      viewportElement.removeEventListener(Enums.Events.STACK_NEW_IMAGE, runOnce)
    );
  }
  eventTarget.addEventListener(Enums.Events.STACK_NEW_IMAGE, runOnce);
  cleanups.push(() => eventTarget.removeEventListener(Enums.Events.STACK_NEW_IMAGE, runOnce));

  const timer = setTimeout(runOnce, 1500);
  cleanups.push(() => clearTimeout(timer));
}

const ViewerSettings = ({ commandsManager, uiNotificationService }) => {
  const [searchParams] = useSearchParams();

  // Orientation auto-correction + window/level restore on load and navigation.
  useEffect(() => {
    let windowApplied = false;
    let windowResolved = false; // study confirmed to have no values for this shot

    const tick = async () => {
      const viewportElement = document.querySelector('.cornerstone-viewport-element');
      if (!viewportElement) {
        return;
      }
      const enabledElement = getEnabledElement(viewportElement);
      if (!enabledElement || !enabledElement.viewport) {
        return;
      }
      const viewport = enabledElement.viewport;
      const imageId = getCurrentImageId(viewport);
      if (!imageId) {
        return;
      }
      const dicomMetaData = classes.MetadataProvider.get('instance', imageId);
      const sopInstanceUID = dicomMetaData?.SOPInstanceUID;
      if (!sopInstanceUID) {
        return;
      }

      // Orientation: exactly once per shot.
      if (!orientationApplied.has(sopInstanceUID)) {
        if (dicomMetaData.PatientOrientation) {
          adjustOrientation(dicomMetaData, commandsManager);
        }
        orientationApplied.add(sopInstanceUID);
        viewport.render();
      }

      // Window/level restore for this shot.
      if (windowApplied || windowResolved) {
        return;
      }
      const studyInstanceUID = new URLSearchParams(window.location.search).get(
        'StudyInstanceUIDs'
      );
      if (!studyInstanceUID) {
        return;
      }
      const result = await restoreWindowingToViewport(viewport, sopInstanceUID, studyInstanceUID);
      if (result === 'applied') {
        windowApplied = true;
      } else if (result === 'none') {
        windowResolved = true;
      }
    };

    // Apply immediately, then retry at short increasing delays: on a reload the
    // viewport/metadata take a moment to be ready.
    tick();
    const timers = [200, 500, 1000, 1800].map(delay => setTimeout(tick, delay));

    // Re-apply when a different shot becomes the current image (scroll / nav).
    const onNewImage = () => {
      windowApplied = false;
      windowResolved = false;
      setTimeout(tick, 50);
    };
    eventTarget.addEventListener(Enums.Events.STACK_NEW_IMAGE, onNewImage);

    return () => {
      timers.forEach(clearTimeout);
      eventTarget.removeEventListener(Enums.Events.STACK_NEW_IMAGE, onNewImage);
    };
  }, [searchParams, commandsManager]);

  // Window/level SAVE: persist the user's adjustment when they click Save.
  useEffect(() => {
    let element;

    const saveWindowingValues = async () => {
      const viewportElement = document.querySelector('.cornerstone-viewport-element');
      if (!viewportElement) {
        return;
      }
      const enabledElement = getEnabledElement(viewportElement);
      if (!enabledElement || !enabledElement.viewport) {
        return;
      }

      const { lower, upper } = enabledElement.viewport.voiRange || {};
      if (lower === undefined || upper === undefined) {
        return;
      }
      const windowWidth = upper - lower;
      const windowLevel = lower + windowWidth / 2;

      const imageId = getCurrentImageId(enabledElement.viewport);
      const dicomMetaData = imageId
        ? classes.MetadataProvider.get('instance', imageId)
        : null;
      const sopInstanceUID = dicomMetaData?.SOPInstanceUID;
      if (!sopInstanceUID) {
        return;
      }

      const studyInstanceUID = new URLSearchParams(window.location.search).get(
        'StudyInstanceUIDs'
      );
      if (!studyInstanceUID) {
        return;
      }

      const savingNotificationId = uiNotificationService?.show?.({
        title: 'Window Settings',
        message: 'Saving window settings…',
        type: 'info',
        duration: 1500,
      });

      try {
        await updateWindowingData(studyInstanceUID, sopInstanceUID, windowWidth, windowLevel);
        if (savingNotificationId && uiNotificationService?.hide) {
          uiNotificationService.hide(savingNotificationId);
        }
        uiNotificationService?.show?.({
          title: 'Window Settings',
          message: 'Window settings saved.',
          type: 'success',
          duration: 2000,
        });
      } catch (error) {
        console.error('ViewerSettings: failed to save windowing values:', error);
        if (savingNotificationId && uiNotificationService?.hide) {
          uiNotificationService.hide(savingNotificationId);
        }
        uiNotificationService?.show?.({
          title: 'Window Settings',
          message: 'Could not save window settings. Please try again.',
          type: 'error',
          duration: 4000,
        });
      }
    };

    const attach = async () => {
      element = await waitForElement('.cornerstone-viewport-element');
      element.addEventListener('saveWindowingValues', saveWindowingValues);
    };
    attach();

    return () => {
      if (element) {
        element.removeEventListener('saveWindowingValues', saveWindowingValues);
      }
    };
  }, [searchParams, uiNotificationService]);

  return null;
};

function waitForElement(selector) {
  return new Promise(resolve => {
    const existing = document.querySelector(selector);
    if (existing) {
      resolve(existing);
      return;
    }

    const observer = new MutationObserver(() => {
      const element = document.querySelector(selector);
      if (element) {
        resolve(element);
        observer.disconnect();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  });
}

export default ViewerSettings;
